import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { faCheckCircle, faInfoCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import { useCallback } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import type { VolunteerActivityType } from '@/queries/constants'
import * as url from '@/client/services/url'
import { VOLUNTEER_ACTIVITY_TO_LABEL } from '@/client/services/volunteerActivity'

import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import RichText from '@/components/UI/RichTextEditor/RichText'
import Tippy from '@/components/UI/Tippy'

import { ChangesRequestedBanner } from './ActivityModal/ChangesRequestedBanner'
import { VOLUNTEER_ACTIVITIES_METADATA } from './constants'
import { ACTIVITY_TYPE_QUERY_PARAM, SubmitForm } from './SubmitForm'
import type { VolunteerActivityComponentAttributes, VolunteerActivityExamples } from './types'

function ExamplesColumn({
  examples,
  variant,
  title,
}: {
  examples: VolunteerActivityExamples
  variant: 'ok' | 'not-ok'
  title: string
}): JSX.Element {
  const { key, titleColor, itemsColor, icon } =
    variant === 'ok'
      ? {
          key: 'ok-to-track',
          titleColor: 'green.muted',
          itemsColor: 'success.fg',
          icon: faCheckCircle,
        }
      : {
          key: 'not-ok-to-track',
          titleColor: 'red.muted',
          itemsColor: 'error.fg',
          icon: faTimesCircle,
        }

  return (
    <Stack direction="column">
      <Text bgColor={titleColor} fontWeight={600} borderRadius="8px" p={2}>
        {title}
      </Text>
      {examples.map(({ key: exampleKey, text, tooltip }) => (
        <Stack key={`${key}_${exampleKey}`} direction="row" spacing={2} align="start">
          <Box>
            <FontAwesomeIcon icon={icon} color={chakraTokenToVar(itemsColor)} />
          </Box>
          <Text>
            {text}
            {tooltip != null ? (
              <Tippy content={tooltip}>
                <Box as="span" ml={2}>
                  <FontAwesomeIcon icon={faInfoCircle} color={chakraTokenToVar('fg.subtle')} />
                </Box>
              </Tippy>
            ) : null}
          </Text>
        </Stack>
      ))}
    </Stack>
  )
}

function Examples({
  okToTrack,
  notOkToTrack,
}: {
  okToTrack?: VolunteerActivityExamples
  notOkToTrack?: VolunteerActivityExamples
}): JSX.Element {
  return (
    <Flex justify="space-between" gap={4} mt={4} w="100%">
      {okToTrack ? (
        <Box w="50%">
          <ExamplesColumn examples={okToTrack} title="OK to track" variant="ok" />
        </Box>
      ) : null}
      {notOkToTrack ? (
        <Box w="50%">
          <ExamplesColumn examples={notOkToTrack} title="Not OK to track" variant="not-ok" />
        </Box>
      ) : null}
    </Flex>
  )
}

export function CreateOrUpdateActivityModal({
  open,
  activity,
  onClose,
}: {
  open: boolean
  activity?: VolunteerActivityComponentAttributes | null
  onClose: () => void
}): JSX.Element {
  const router = useRouter()
  const isUpdateMode = activity != null

  const queryParamType = router.query[ACTIVITY_TYPE_QUERY_PARAM]
  const selectedType: VolunteerActivityType | null =
    typeof queryParamType === 'string'
      ? (queryParamType as VolunteerActivityType)
      : (activity?.type ?? null)

  const onCloseCallback = useCallback(async () => {
    const { [ACTIVITY_TYPE_QUERY_PARAM]: existingTypeParam, ...rest } = router.query
    if (existingTypeParam != null) {
      await router.replace({ query: rest }, undefined, { scroll: false })
    }
    onClose()
  }, [onClose, router])

  const metadata = selectedType != null ? VOLUNTEER_ACTIVITIES_METADATA[selectedType] : null

  const activityLabel =
    selectedType != null ? `"${VOLUNTEER_ACTIVITY_TO_LABEL[selectedType]}"` : null

  let title: string
  if (isUpdateMode) {
    title = `Update ${activityLabel ?? 'Volunteer'} Activity`
  } else if (activityLabel != null) {
    title = `Submit ${activityLabel} Activity`
  } else {
    title = 'Submit Manual Activity'
  }

  return (
    <ModalV3
      open={open}
      title={title}
      size="3xl"
      body={() => (
        <Stack direction="column" spacing={4}>
          {!isUpdateMode ? (
            <Alert variant="warning" preserveFontWeight>
              It is essential to report hours with integrity. The Schoolhouse team regularly reviews
              hours and your account could face suspension if you try to circumvent policies or go
              against the spirit of volunteer hours. To learn more about Schoolhouse&apos;s
              policies, visit the{' '}
              <ShwLink href={url.faq.getUrl('volunteering')}>Volunteer Hour FAQs</ShwLink>.
            </Alert>
          ) : null}
          {activity != null ? (
            <ChangesRequestedBanner
              reviewer={activity.reviewer}
              requestedChanges={activity.requested_changes}
              requestedChangesAt={activity.requested_changes_at}
              lastSubmittedAt={activity.last_submitted_at}
            />
          ) : null}
          {metadata != null ? (
            <Stack direction="column" spacing={2} borderRadius="8px" p={3} bgColor="bg.muted">
              {metadata.generalNote == null ? null : <RichText content={metadata.generalNote} />}
              <Box>
                {metadata.linkToFAQ != null ? (
                  <>
                    Learn more in this{' '}
                    <ShwLink isExternal href={metadata.linkToFAQ}>
                      FAQ article
                    </ShwLink>
                    .
                  </>
                ) : null}
              </Box>

              <Examples okToTrack={metadata.okToTrack} notOkToTrack={metadata.notOkToTrack} />
            </Stack>
          ) : null}
          <SubmitForm onSubmit={onCloseCallback} onCancel={onCloseCallback} activity={activity} />
        </Stack>
      )}
      onClose={onCloseCallback}
    />
  )
}
